import { matchPath } from 'react-router-dom';

// Help content is compiled from docs/user-guide/articles by tools/user-guide/build-help.js into public/help-content/help.json.
export const HELP_BASE = `${process.env.PUBLIC_URL || ''}/help-content`;

let pending = null;

export function loadHelp(fetcher = (url) => fetch(url).then((r) => (r.ok ? r.json() : Promise.reject(new Error(`Help content ${r.status}`))))) {
  if (!pending) {
    pending = fetcher(`${HELP_BASE}/help.json`).then(indexHelp, (error) => {
      pending = null;
      throw error;
    });
  }
  return pending;
}

export function resetHelpCache() {
  pending = null;
}

export function indexHelp(help) {
  const byId = new Map(help.articles.map((a) => [a.id, a]));
  const byCategory = new Map(help.categories.map((c) => [c.id, help.articles.filter((a) => a.category === c.id)]));
  return { ...help, byId, byCategory };
}

const words = (text) =>
  (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .split(/[^a-z0-9]+/)
    .filter(Boolean);

const FIELDS = [
  ['title', 12],
  ['keywords', 8],
  ['headings', 5],
  ['summary', 4],
  ['text', 1],
];

function fieldText(article, field) {
  if (field === 'keywords') return article.keywords.join(' ');
  if (field === 'headings') return article.headings.map((h) => h.text).join(' ');
  return article[field];
}

/** Articles ranked by how well every query word matches (prefix match, title and keywords count most). */
export function searchHelp(help, query, limit = 20) {
  const terms = words(query);
  if (!help || terms.length === 0) return [];
  const results = [];
  for (const article of help.articles) {
    let score = 0;
    let matchedAll = true;
    for (const term of terms) {
      let termScore = 0;
      for (const [field, weight] of FIELDS) {
        const tokens = words(fieldText(article, field));
        if (tokens.includes(term)) termScore += weight * 2;
        else if (tokens.some((t) => t.startsWith(term))) termScore += weight;
      }
      if (termScore === 0) {
        matchedAll = false;
        break;
      }
      score += termScore;
    }
    if (matchedAll) results.push({ article, score, snippet: snippet(article, terms) });
  }
  return results.sort((a, b) => b.score - a.score || a.article.title.localeCompare(b.article.title)).slice(0, limit);
}

function snippet(article, terms) {
  const text = article.text || '';
  const lower = text.toLowerCase();
  const at = terms.map((t) => lower.indexOf(t)).filter((i) => i >= 0).sort((a, b) => a - b)[0];
  if (at === undefined || article.summary.toLowerCase().includes(terms[0])) return article.summary;
  const start = Math.max(0, text.lastIndexOf(' ', Math.max(0, at - 60)) + 1);
  const end = Math.min(text.length, at + 120);
  return `${start > 0 ? '…' : ''}${text.slice(start, end).trim()}${end < text.length ? '…' : ''}`;
}

/** Articles whose routes match the current path, most specific route first. */
export function articlesForPath(help, pathname) {
  if (!help) return [];
  const matches = [];
  for (const article of help.articles) {
    const best = article.routes
      .filter((route) => matchPath({ path: route, end: true }, pathname))
      .sort((a, b) => b.length - a.length)[0];
    if (best) matches.push({ article, specificity: best.split('/').length + (best.includes(':') ? 0 : 0.5) });
  }
  return matches.sort((a, b) => b.specificity - a.specificity || a.article.order - b.article.order).map((m) => m.article);
}

export const imageUrl = (src) => `${HELP_BASE}/${src}`;

export function neighbours(help, article) {
  const siblings = help.byCategory.get(article.category) || [];
  const index = siblings.findIndex((a) => a.id === article.id);
  return { previous: index > 0 ? siblings[index - 1] : null, next: index >= 0 && index < siblings.length - 1 ? siblings[index + 1] : null };
}

export const ROLE_NAMES = { everyone: 'Everyone', supervisor: 'Supervisors', manager: 'Managers', admin: 'Administrators' };
